import { OrthographicCamera, type Vector2 } from 'three';
import { worldPerScreenPx } from '@/lib/threeScene/cameras/orthoUnits';
import { XRAY_CAMERA_TARGET_DISTANCE } from '@/views/dicoms/xrays/xraySceneState';

/** Extra magnification the loupe applies on top of the X-ray's current zoom. */
export const LOUPE_MAGNIFICATION = 2;

/** Loupe circle diameter in CSS pixels (matches Figma). */
export const LOUPE_DIAMETER_PX = 100;

export type LoupeFrustum = { left: number; right: number; top: number; bottom: number };

/**
 * Symmetric orthographic frustum bounds (world units) for the square loupe
 * canvas. Across the loupe's width it spans
 * `mainWorldPerPx * diameterPx / magnification` world units, so it shows the
 * scene at `magnification`x the main view.
 */
export function loupeCameraFrustum(args: {
    mainWorldPerPx: number;
    diameterPx: number;
    magnification: number;
}): LoupeFrustum {
    const halfExtent = (args.mainWorldPerPx * args.diameterPx) / (2 * args.magnification);
    return { left: -halfExtent, right: halfExtent, top: halfExtent, bottom: -halfExtent };
}

/**
 * Point the loupe camera at `target` world position, magnifying the same scene
 * the main camera renders. Mutates `loupeCamera` in place.
 */
export function updateLoupeCamera(
    loupeCamera: OrthographicCamera,
    args: {
        mainCamera: OrthographicCamera;
        mainCanvasHeightPx: number;
        target: Vector2;
        diameterPx?: number;
        magnification?: number;
    },
): void {
    const diameterPx = args.diameterPx ?? LOUPE_DIAMETER_PX;
    const magnification = args.magnification ?? LOUPE_MAGNIFICATION;
    const mainWorldPerPx = worldPerScreenPx(args.mainCamera, args.mainCanvasHeightPx);
    const { left, right, top, bottom } = loupeCameraFrustum({
        mainWorldPerPx,
        diameterPx,
        magnification,
    });
    loupeCamera.left = left;
    loupeCamera.right = right;
    loupeCamera.top = top;
    loupeCamera.bottom = bottom;
    loupeCamera.near = args.mainCamera.near;
    loupeCamera.far = args.mainCamera.far;
    loupeCamera.zoom = 1;
    loupeCamera.up.set(0, 1, 0);
    loupeCamera.position.set(args.target.x, args.target.y, XRAY_CAMERA_TARGET_DISTANCE);
    loupeCamera.lookAt(args.target.x, args.target.y, 0);
    loupeCamera.updateProjectionMatrix();
}
